import { useEffect, useRef, useState } from 'react';

export function isOnline() {
  if (typeof navigator === 'undefined') return true;
  return navigator.onLine !== false;
}

export function useOnlineStatus() {
  const [online, setOnline] = useState(() => isOnline());

  useEffect(() => {
    const updateOnlineStatus = () => setOnline(isOnline());
    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);
    return () => {
      window.removeEventListener('online', updateOnlineStatus);
      window.removeEventListener('offline', updateOnlineStatus);
    };
  }, []);

  return online;
}

export function useOnlineRefreshSignal({ throttleMs = 1500 } = {}) {
  const [signal, setSignal] = useState(0);
  const lastRefreshAt = useRef(0);

  useEffect(() => {
    const refresh = (event) => {
      if (!isOnline()) return;
      const now = Date.now();
      const force = event?.type === 'online' || event?.type === 'membernest:online-refresh';
      if (!force && now - lastRefreshAt.current < throttleMs) return;
      lastRefreshAt.current = now;
      setSignal((value) => value + 1);
    };
    const refreshIfVisible = (event) => {
      if (document.visibilityState === 'visible') refresh(event);
    };

    window.addEventListener('online', refresh);
    window.addEventListener('membernest:online-refresh', refresh);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refreshIfVisible);
    return () => {
      window.removeEventListener('online', refresh);
      window.removeEventListener('membernest:online-refresh', refresh);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refreshIfVisible);
    };
  }, [throttleMs]);

  return signal;
}

export function useConnectionStatus({
  reconnectingDurationMs = 1500,
  onlineVisibleMs = 10000,
} = {}) {
  const online = useOnlineStatus();
  const initialOnline = useRef(isOnline());
  const previousOnline = useRef(initialOnline.current);
  const [status, setStatus] = useState(() => (initialOnline.current ? null : 'offline'));

  useEffect(() => {
    let reconnectingTimer;
    let onlineTimer;

    if (!online) {
      previousOnline.current = false;
      setStatus('offline');
      return undefined;
    }

    if (previousOnline.current === false) {
      setStatus('reconnecting');
      reconnectingTimer = window.setTimeout(() => {
        setStatus('online');
        onlineTimer = window.setTimeout(() => setStatus(null), onlineVisibleMs);
      }, reconnectingDurationMs);
    } else {
      setStatus(null);
    }

    previousOnline.current = true;

    return () => {
      window.clearTimeout(reconnectingTimer);
      window.clearTimeout(onlineTimer);
    };
  }, [online, onlineVisibleMs, reconnectingDurationMs]);

  return status;
}

export function offlineWriteMessage(action = 'save changes') {
  return `You are offline. Reconnect to ${action}.`;
}

const MUTATING_CONTROL_PATTERN = /\b(add|allocate|approve|assign|book|cancel|checkout|confirm|create|delete|disable|edit|enable|invite|join|pay|publish|refund|reject|remove|request|respond|rsvp|save|send|submit|sync|turn off|turn on|update)\b/i;

export function isLikelyMutatingControl(element) {
  if (!element) return false;
  const type = String(element.getAttribute?.('type') || '').toLowerCase();
  if (type === 'submit') return true;

  const label = [
    element.getAttribute?.('aria-label'),
    element.getAttribute?.('title'),
    element.textContent,
    element.value,
  ].filter(Boolean).join(' ');

  return MUTATING_CONTROL_PATTERN.test(label);
}
